import { cn } from "@app/ui";
import { memo, useMemo, type CSSProperties, type ReactNode } from "react";
import { parseMarkdown, type MarkdownBlock, type MarkdownInline, type MarkdownListItem } from "./markdown.js";

interface Props {
  value: string;
  compact?: boolean | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

export const MarkdownPreview = memo(function MarkdownPreview({ value, compact = false, className, style }: Props) {
  const blocks = useMemo(() => parseMarkdown(value), [value]);
  if (blocks.length === 0) return null;

  return (
    <div
      className={cn(
        "min-w-0 break-words text-[inherit] leading-[inherit]",
        compact ? "space-y-0.5" : "space-y-1.5",
        className,
      )}
      style={style}
    >
      {blocks.map((block, index) => renderBlock(block, `${index}`, compact))}
    </div>
  );
});

function renderBlock(block: MarkdownBlock, key: string, compact: boolean): ReactNode {
  switch (block.kind) {
    case "paragraph":
      return <p key={key} className="my-0 whitespace-pre-wrap">{renderInline(block.children, key)}</p>;
    case "heading": {
      const className = cn(
        "my-0 font-semibold leading-tight",
        compact ? "text-[1em]" : block.level === 1 ? "text-base" : block.level === 2 ? "text-[0.98em]" : "text-[0.92em]",
      );
      if (block.level === 1) return <h3 key={key} className={className}>{renderInline(block.children, key)}</h3>;
      if (block.level === 2) return <h4 key={key} className={className}>{renderInline(block.children, key)}</h4>;
      return <h5 key={key} className={className}>{renderInline(block.children, key)}</h5>;
    }
    case "list": {
      const List = block.ordered ? "ol" : "ul";
      return (
        <List
          key={key}
          className={cn(
            "my-0 marker:opacity-70",
            block.ordered ? "list-decimal" : "list-disc",
            compact ? "space-y-0 pl-3" : "space-y-0.5 pl-5",
          )}
        >
          {block.items.map((item, index) => renderListItem(item, `${key}-${index}`, compact))}
        </List>
      );
    }
    case "quote":
      return (
        <blockquote key={key} className={cn("my-0 border-l-2 border-current/25 pl-2 italic", compact ? "space-y-0.5" : "space-y-1")}>
          {block.blocks.map((child, index) => renderBlock(child, `${key}-q-${index}`, compact))}
        </blockquote>
      );
    case "code":
      return (
        <pre key={key} className={cn("my-0 overflow-auto rounded bg-black/10 px-2 py-1 font-mono text-[0.92em] not-italic dark:bg-white/10", compact && "px-1 py-0.5")}>
          <code>{block.text}</code>
        </pre>
      );
    case "rule":
      return <hr key={key} className="my-1 border-current/20" />;
  }
}

function renderListItem(item: MarkdownListItem, key: string, compact: boolean): ReactNode {
  if (item.checked === undefined) {
    return <li key={key}>{renderInline(item.children, key)}</li>;
  }

  return (
    <li key={key} className={cn("list-none", compact ? "-ml-3" : "-ml-5")}>
      <span className="inline-flex min-w-0 items-start gap-1.5 align-top">
        <input
          type="checkbox"
          checked={item.checked}
          readOnly
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          className="mt-[0.15em] h-[0.9em] w-[0.9em] shrink-0 rounded border-current/40 accent-current"
          aria-label={item.checked ? "Checked" : "Unchecked"}
        />
        <span className="min-w-0">{renderInline(item.children, key)}</span>
      </span>
    </li>
  );
}

function renderInline(nodes: MarkdownInline[], key: string): ReactNode[] {
  return nodes.map((node, index) => {
    const nodeKey = `${key}-i-${index}`;
    switch (node.kind) {
      case "text":
        return node.text;
      case "break":
        return <br key={nodeKey} />;
      case "code":
        return <code key={nodeKey} className="rounded bg-black/10 px-1 font-mono text-[0.92em] dark:bg-white/10">{node.text}</code>;
      case "strong":
        return <strong key={nodeKey} className="font-semibold">{renderInline(node.children, nodeKey)}</strong>;
      case "em":
        return <em key={nodeKey}>{renderInline(node.children, nodeKey)}</em>;
      case "delete":
        return <del key={nodeKey}>{renderInline(node.children, nodeKey)}</del>;
      case "link":
        return (
          <a
            key={nodeKey}
            href={node.href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(event) => event.stopPropagation()}
            className="underline underline-offset-2 hover:opacity-80"
          >
            {renderInline(node.children, nodeKey)}
          </a>
        );
    }
  });
}
